import { useState, useEffect } from 'react'
import { Box, Flex, VStack, Grid, Input, Spinner } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller, useFormColors, FormSection, FormHeader, WizardActions, FormLabel, FormTabs } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LuBuilding2 as Building2,
  LuFileText as FileText,
  LuCheck as Check,
  LuSearch as Search,
} from 'react-icons/lu'
import { toaster, Text, useT } from '@minetrace/ui'
import { plantasService, scrapingService } from '../../api/services'
import { useRequest } from '../../hooks'
import type { PlantaResponse } from '../../api/models/response/planta'
import type { CompanyPersonResponse } from '../../api/models/response'
import ContactosSection from '../../components/ContactosSection'
import UbigeoSelect from '../../components/UbigeoSelect'
import CharCounter from '../../components/CharCounter'
import { ECONOMIC_ACTIVITIES } from '../../data/enums'
import { CompanyFieldLengths } from '../../lib/fieldLengths'
import { phoneRegister } from '../../lib/inputSanitizers'

type TabType = 'empresa' | 'contactos' | 'documentos'

// ── Schemas ──
const step1Schema = z.object({
  ruc: z.string().length(11, 'plantas.nuevo.zod.rucLength'),
  legalName: z.string().min(1, 'plantas.nuevo.zod.legalNameRequired'),
  commercialName: z.string().optional(),
  economicActivity: z.number().min(1, 'plantas.nuevo.zod.economicActivityRequired'),
  taxAddress: z.string().min(1, 'plantas.nuevo.zod.taxAddressRequired'),
  ubigeoCode: z.string().length(6, 'plantas.nuevo.zod.ubigeoRequired'),
  mainPhone: z.string().optional(),
  mainEmail: z.string().email('plantas.nuevo.zod.emailInvalid').optional().or(z.literal('')),
  website: z.string().optional(),
})

const fullSchema = step1Schema

type FullFormData = z.infer<typeof fullSchema>

export default function NuevaPlantaPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()
  const isEditing = Boolean(id)
  const [loadedStatus, setLoadedStatus] = useState(0)

  const [activeTab, setActiveTab] = useState<TabType>('empresa')
  const [loadingData, setLoadingData] = useState(isEditing)
  const [sunatConsulted, setSunatConsulted] = useState(false)

  // Wizard state
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [completedSteps, setCompletedSteps] = useState<Set<TabType>>(new Set())

  // Contacts state (step 2)
  const [persons, setPersons] = useState<CompanyPersonResponse[]>([])

  // ── useRequest hooks ──
  const { execute: consultarSunat, isFetching: isSearchingSunat } = useRequest(scrapingService.consultarSunat)
  const { execute: execCreateStep1 } = useRequest(plantasService.createStep1)
  const { execute: execFinalize } = useRequest(
    async (id: string) => {
      await plantasService.finalize(id)
      return true as const
    },
    { successMessage: t('plantas.nuevo.toasts.created') },
  )

  const formMethods = useForm<FullFormData>({
    resolver: zodResolver(fullSchema),
    defaultValues: {
      ruc: '',
      legalName: '',
      commercialName: '',
      economicActivity: 0,
      taxAddress: '',
      ubigeoCode: '',
      mainPhone: '',
      mainEmail: '',
      website: '',
    },
  })
  const {
    register,
    watch,
    setValue,
    control,
    reset,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = formMethods

  // Helper to populate form from PlantaResponse
  const populateForm = (data: PlantaResponse) => {
    reset({
      ruc: data.ruc,
      legalName: data.legalName,
      commercialName: data.commercialName ?? '',
      economicActivity: data.economicActivity ?? 0,
      taxAddress: data.taxAddress ?? '',
      ubigeoCode: data.ubigeoCode ?? '',
      mainPhone: data.mainPhone ?? '',
      mainEmail: data.mainEmail ?? '',
      website: data.website ?? '',
    })
    setPersons(data.persons ?? [])
  }

  // Load existing planta for edit mode
  useEffect(() => {
    if (!id) return
    const loadData = async () => {
      setLoadingData(true)
      try {
        const data = await plantasService.getById(id)
        setLoadedStatus(data.status)
        setCompanyId(data.id)
        populateForm(data)
      } catch {
        // silently fail - form will show empty
      } finally {
        setLoadingData(false)
      }
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reset])


  // ── SUNAT lookup ──
  const handleConsultarSunat = async () => {
    const ruc = watch('ruc')
    if (!ruc || ruc.length !== 11) return
    const data = await consultarSunat(ruc)
    if (!data) return
    if (data.razonSocial) setValue('legalName', data.razonSocial)
    if (data.nombreComercial) setValue('commercialName', data.nombreComercial)
    if (data.direccion) setValue('taxAddress', data.direccion)
    setSunatConsulted(true)
  }

  const handleWizardStep = async (data: FullFormData) => {
    if (activeTab === 'empresa') {
      const result = await execCreateStep1({
        id: companyId ?? id ?? undefined,
        ruc: data.ruc,
        legalName: data.legalName,
        commercialName: data.commercialName || undefined,
        economicActivity: data.economicActivity || undefined,
        taxAddress: data.taxAddress || undefined,
        ubigeoCode: data.ubigeoCode || undefined,
        mainPhone: data.mainPhone || undefined,
        mainEmail: data.mainEmail || undefined,
        website: data.website || undefined,
        sunatVerified: sunatConsulted || undefined,
      })
      if (!result) return

      setCompanyId(result.id)
      setCompletedSteps((prev) => new Set(prev).add('empresa'))
      setActiveTab('contactos')
    } else if (activeTab === 'contactos') {
      // Step 2: contacts are managed via add/remove, just advance
      setCompletedSteps((prev) => new Set(prev).add('contactos'))
      setActiveTab('documentos')
    } else if (activeTab === 'documentos') {
      // Step 3: Finalize
      const cid = companyId ?? id
      if (!cid) {
        toaster.error({ description: t('plantas.nuevo.errors.noPlantaInProcess') })
        return
      }

      const result = await execFinalize(cid)
      if (!result) return

      setCompletedSteps((prev) => new Set(prev).add('documentos'))
      setTimeout(() => navigate('/app/plantas'), 1500)
    }
  }

  // ── Wizard tab navigation guard ──
  const handleTabChange = (tab: TabType) => {
    if (isEditing) {
      setActiveTab(tab)
      return
    }
    const tabOrder: TabType[] = ['empresa', 'contactos', 'documentos']
    const tabIndex = tabOrder.indexOf(tab)
    const currentIndex = tabOrder.indexOf(activeTab)

    if (tabIndex <= currentIndex) {
      setActiveTab(tab)
      return
    }
    if (completedSteps.has(tabOrder[tabIndex - 1]!)) {
      setActiveTab(tab)
    }
  }

  const handleBack = () => {
    const tabOrder: TabType[] = ['empresa', 'contactos', 'documentos']
    const idx = tabOrder.indexOf(activeTab)
    if (idx > 0) setActiveTab(tabOrder[idx - 1]!)
  }

  const handleSaveAndExit = () => {
    if (!isEditing || !id) return
    navigate('/app/plantas')
  }

  // ── Per-step fields for trigger validation ──
  const STEP_FIELDS: Record<TabType, (keyof FullFormData)[]> = {
    empresa: ['ruc', 'legalName', 'mainEmail', 'economicActivity', 'taxAddress', 'ubigeoCode'],
    contactos: [],
    documentos: [],
  }

  const handleStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fields = STEP_FIELDS[activeTab]
    const isValid = fields.length === 0 || (await trigger(fields))
    if (isValid) {
      await handleWizardStep(getValues())
    }
  }

  // ── Styles ──
  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const errorInputStyles = {
    ...inputStyles,
    ring: '1px',
    ringColor: c.error,
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const empresaHasErrors = Boolean(errors.ruc || errors.legalName || errors.mainEmail || errors.economicActivity || errors.taxAddress || errors.ubigeoCode)

  const tabs = [
    { key: 'empresa' as const, label: t('plantas.nuevo.tabs.empresa'), hasError: empresaHasErrors },
    { key: 'contactos' as const, label: t('plantas.nuevo.tabs.contactos', { count: persons.length }), hasError: false },
    { key: 'documentos' as const, label: t('plantas.nuevo.tabs.documentos'), hasError: false },
  ]

  if (loadingData) {
    return (
      <VStack gap={6} alignItems="stretch">
        <FormHeader
          title={t('plantas.nuevo.loadingTitle')}
          subtitle={t('plantas.nuevo.loadingSubtitle')}
          backPath="/app/empresas"
        />
        <Box p={8} textAlign="center">
          <Text tid="plantas.nuevo.loadingData" color={c.textMuted} />
        </Box>
      </VStack>
    )
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <FormHeader
        title={isEditing ? t('plantas.nuevo.titleEdit') : t('plantas.nuevo.titleNew')}
        subtitle={isEditing ? t('plantas.nuevo.subtitleEdit') : t('plantas.nuevo.subtitleNew')}
        backPath="/app/empresas"
      />

      <FormTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      <FormProvider {...formMethods}>
      <form onSubmit={handleStepSubmit}>
        {/* Tab: Datos Empresa */}
        {activeTab === 'empresa' && (
          <VStack gap={6} alignItems="stretch">
            <FormSection icon={Building2} titleTid="plantas.nuevo.sections.empresaInfo">
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                {/* RUC with SUNAT button */}
                <Box>
                  <FormLabel required>{t('plantas.nuevo.fields.ruc')}</FormLabel>
                  <Flex gap={2}>
                    <Input
                      {...register('ruc')}
                      placeholder={t('plantas.nuevo.fields.rucPlaceholder')}
                      maxLength={CompanyFieldLengths.ruc}
                      fontFamily="monospace"
                      {...(errors.ruc ? errorInputStyles : inputStyles)}
                    />
                    <Box
                      as="button"
                      type="button"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      px={4}
                      bg={c.accentGradient}
                      color="white"
                      borderRadius="xl"
                      cursor={watch('ruc')?.length !== 11 || isSearchingSunat ? 'not-allowed' : 'pointer'}
                      opacity={watch('ruc')?.length !== 11 || isSearchingSunat ? 0.5 : 1}
                      flexShrink={0}
                      minW="48px"
                      onClick={handleConsultarSunat}
                      disabled={watch('ruc')?.length !== 11 || isSearchingSunat}
                    >
                      {isSearchingSunat ? <Spinner size="sm" color="white" /> : <Search size={18} />}
                    </Box>
                  </Flex>
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    <Text tid="plantas.nuevo.fields.rucHelp" fontSize="xs" color={c.textMuted} />
                    <CharCounter name="ruc" max={CompanyFieldLengths.ruc} mt={0} />
                  </Flex>
                  {errors.ruc && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.ruc.message ?? '')}</Text>
                  )}
                </Box>

                {/* Razon Social */}
                <Box>
                  <FormLabel required>{t('plantas.nuevo.fields.legalName')}</FormLabel>
                  <Input
                    {...register('legalName')}
                    placeholder={t('plantas.nuevo.fields.legalNamePlaceholder')}
                    maxLength={CompanyFieldLengths.legalName}
                    {...(errors.legalName ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.legalName ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.legalName.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="legalName" max={CompanyFieldLengths.legalName} mt={0} />
                  </Flex>
                </Box>

                {/* Nombre Comercial */}
                <Box>
                  <FormLabel>{t('plantas.nuevo.fields.commercialName')}</FormLabel>
                  <Input
                    {...register('commercialName')}
                    placeholder={t('plantas.nuevo.fields.commercialNamePlaceholder')}
                    maxLength={CompanyFieldLengths.commercialName}
                    {...inputStyles}
                  />
                  <CharCounter name="commercialName" max={CompanyFieldLengths.commercialName} />
                </Box>

                {/* Actividad Economica */}
                <Box>
                  <FormLabel required>{t('plantas.nuevo.fields.economicActivity')}</FormLabel>
                  <Controller
                    name="economicActivity"
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        style={selectStyles}
                      >
                        {ECONOMIC_ACTIVITIES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.economicActivity && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.economicActivity.message ?? '')}</Text>
                  )}
                </Box>

                {/* Direccion Fiscal */}
                <Box gridColumn={{ md: 'span 2' }}>
                  <FormLabel required>{t('plantas.nuevo.fields.taxAddress')}</FormLabel>
                  <Input
                    {...register('taxAddress')}
                    placeholder={t('plantas.nuevo.fields.taxAddressPlaceholder')}
                    maxLength={CompanyFieldLengths.taxAddress}
                    {...(errors.taxAddress ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.taxAddress ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.taxAddress.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="taxAddress" max={CompanyFieldLengths.taxAddress} mt={0} />
                  </Flex>
                </Box>

                {/* Ubigeo */}
                <Box gridColumn={{ md: 'span 2' }}>
                  <FormLabel required>{t('plantas.nuevo.fields.ubigeo')}</FormLabel>
                  <Controller
                    name="ubigeoCode"
                    control={control}
                    render={({ field }) => (
                      <UbigeoSelect
                        value={field.value ?? ''}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.ubigeoCode && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.ubigeoCode.message ?? '')}</Text>
                  )}
                </Box>

                {/* Telefono */}
                <Box>
                  <FormLabel>{t('plantas.nuevo.fields.phone')}</FormLabel>
                  <Input
                    {...phoneRegister(register('mainPhone'))}
                    placeholder={t('plantas.nuevo.fields.phonePlaceholder')}
                    maxLength={CompanyFieldLengths.mainPhone}
                    {...inputStyles}
                  />
                  <CharCounter name="mainPhone" max={CompanyFieldLengths.mainPhone} />
                </Box>

                {/* Email */}
                <Box>
                  <FormLabel>{t('plantas.nuevo.fields.email')}</FormLabel>
                  <Input
                    type="email"
                    {...register('mainEmail')}
                    placeholder={t('plantas.nuevo.fields.emailPlaceholder')}
                    maxLength={CompanyFieldLengths.mainEmail}
                    {...(errors.mainEmail ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.mainEmail ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.mainEmail.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="mainEmail" max={CompanyFieldLengths.mainEmail} mt={0} />
                  </Flex>
                </Box>

                {/* Website */}
                <Box gridColumn={{ md: 'span 2' }}>
                  <FormLabel>{t('plantas.nuevo.fields.website')}</FormLabel>
                  <Input
                    {...register('website')}
                    placeholder={t('plantas.nuevo.fields.websitePlaceholder')}
                    maxLength={CompanyFieldLengths.website}
                    {...inputStyles}
                  />
                  <CharCounter name="website" max={CompanyFieldLengths.website} />
                </Box>
              </Grid>
            </FormSection>
          </VStack>
        )}

        {/* Tab: Contactos */}
        {activeTab === 'contactos' && (
          <ContactosSection
            companyId={companyId}
            persons={persons}
            onPersonsChange={setPersons}
            addPerson={plantasService.addPerson}
            removePerson={plantasService.removePerson}
            description={t('plantas.nuevo.contactosDescription')}
          />
        )}

        {/* Tab: Documentos */}
        {activeTab === 'documentos' && (
          <VStack gap={6} alignItems="stretch">
            <FormSection icon={FileText} titleTid="plantas.nuevo.sections.documentos" descriptionTid="plantas.nuevo.sections.documentosDescription">
              <Box p={6} textAlign="center" borderRadius="xl" border="1px dashed" borderColor={c.border}>
                <Box
                  display="inline-flex"
                  p={3}
                  borderRadius="full"
                  bg={`${c.accent}20`}
                  color={c.accent}
                  mb={3}
                >
                  <FileText size={24} />
                </Box>
                <Text tid="plantas.nuevo.docsPlaceholder.main" fontSize="sm" color={c.textMuted} mb={1} />
                <Text tid="plantas.nuevo.docsPlaceholder.hint" fontSize="xs" color={c.textMuted} />
              </Box>
            </FormSection>

            {/* Summary before finalize */}
            {!isEditing && (
              <FormSection icon={Check} titleTid="plantas.nuevo.sections.resumen" delay={0.1}>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                  <Box p={4} bg={c.bgInput} borderRadius="xl">
                    <Text tid="plantas.nuevo.fields.empresa" fontSize="xs" color={c.textMuted} mb={1} />
                    <Text fontSize="sm" fontWeight="600" color={c.text}>
                      {watch('legalName') || t('plantas.nuevo.fields.empresaPlaceholder')}
                    </Text>
                    <Text fontSize="xs" color={c.textMuted} fontFamily="monospace" mt={1}>
                      {t('plantas.nuevo.fields.rucLabel', { ruc: watch('ruc') || '---' })}
                    </Text>
                  </Box>
                  <Box p={4} bg={c.bgInput} borderRadius="xl">
                    <Text tid="plantas.nuevo.fields.contactos" fontSize="xs" color={c.textMuted} mb={1} />
                    <Text fontSize="sm" fontWeight="600" color={c.text}>
                      {persons.length === 1
                        ? t('plantas.nuevo.fields.personasSingular', { count: persons.length })
                        : t('plantas.nuevo.fields.personasPlural', { count: persons.length })}
                    </Text>
                  </Box>
                  <Box p={4} bg={c.bgInput} borderRadius="xl">
                    <Text tid="plantas.nuevo.fields.documentos" fontSize="xs" color={c.textMuted} mb={1} />
                    <Text tid="plantas.nuevo.fields.pendiente" fontSize="sm" fontWeight="600" color={c.text} />
                  </Box>
                </Grid>
              </FormSection>
            )}
          </VStack>
        )}

        <WizardActions
          cancelPath="/app/empresas"
          isFirst={activeTab === 'empresa'}
          isLast={activeTab === 'documentos'}
          isEditing={isEditing && loadedStatus !== 0}
          isSubmitting={isSubmitting}
          onBack={handleBack}
          onSaveAndExit={handleSaveAndExit}
        />
      </form>
      </FormProvider>
    </VStack>
  )
}
